import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useScopedTranslation from 'hooks/useScopedTranslation';
import { getSlaSummary } from 'services/escriturasSlaService';
import { escriturasSlaI18n } from './i18n';
import '../../styles/naEeppTableGrid.scss';

// Valores del filtro "Solo con vencidos" (Dropdown, client-side).
const DUE_FILTER_VALUES = ['all', 'due'];

// Formatea un promedio de días hábiles (viene x.y del backend). null → "—".
const formatAvg = (value) => {
  if (value == null || Number.isNaN(Number(value))) return '—';
  const num = Number(value);
  return Number.isInteger(num) ? String(num) : num.toFixed(1);
};

/**
 * Vista "Control SLA" (submenú KPI de EEPP) — espejo del patrón de "Mis escrituras"
 * y "Log de Correos" (DS por prop, AbortController por useRef, Skeleton/empty/error).
 *
 * Muestra el resumen de SLA de todas las escrituras con reloj:
 *  - Tabla por validador: abiertas, vencidas, a tiempo, tarde y promedio de días
 *    hábiles al cierre. Incluye una fila "Sin asignar" (userId null).
 *  - Bloque "Vencidos por etapa": un chip por etapa abierta del flujo con sus
 *    abiertas y vencidas.
 *
 * Filtros client-side del Contract: Input de búsqueda por nombre de validador +
 * Dropdown "Solo con vencidos" (todos | con vencidos).
 */
export default function EscriturasSlaControl({ DesignSystemComponents }) {
  const { t } = useScopedTranslation(escriturasSlaI18n);

  const { Table, Badge, Card, Skeleton, Button, IconButton, Input, Dropdown } =
    DesignSystemComponents ?? {};

  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filtros client-side.
  const [search, setSearch] = useState('');
  const [dueFilter, setDueFilter] = useState('all');

  const abortRef = useRef(null);

  const fetchSummary = useCallback(async () => {
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setLoading(true);
    setError(null);
    try {
      const data = await getSlaSummary(controller.signal);
      setSummary(data && typeof data === 'object' ? data : null);
    } catch (err) {
      if (err.name === 'AbortError' || err.name === 'CanceledError') return;
      setError(err.response?.data?.message || err.message || t('esla_error'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchSummary();
    return () => abortRef.current?.abort();
  }, [fetchSummary]);

  const rows = useMemo(
    () => (Array.isArray(summary?.rows) ? summary.rows : []),
    [summary]
  );
  const dueByStage = useMemo(
    () => (Array.isArray(summary?.dueByStage) ? summary.dueByStage : []),
    [summary]
  );

  const dueOptions = useMemo(
    () =>
      DUE_FILTER_VALUES.map((value) => ({
        label:
          value === 'all' ? t('esla_filter_all') : t('esla_filter_due_only'),
        value,
      })),
    [t]
  );
  const selectedDueOption =
    dueOptions.find((o) => o.value === dueFilter) || dueOptions[0];

  // Resuelve el nombre a mostrar por fila (null = "Sin asignar").
  const rowName = useCallback(
    (row) => row.userName || t('esla_unassigned'),
    [t]
  );

  // Filtrado client-side: por nombre (case-insensitive) y "solo con vencidos".
  const filteredRows = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return rows.filter((row) => {
      if (dueFilter === 'due' && !(row.dueCount > 0)) return false;
      if (needle && !rowName(row).toLowerCase().includes(needle)) return false;
      return true;
    });
  }, [rows, search, dueFilter, rowName]);

  const columns = useMemo(
    () => [
      {
        accessor: 'userName',
        label: t('esla_col_user'),
        render: (row) =>
          row.userName ? (
            row.userName
          ) : (
            <span style={{ color: '#94A3B8' }}>{t('esla_unassigned')}</span>
          ),
      },
      {
        accessor: 'openCount',
        label: t('esla_col_open'),
        render: (row) => row.openCount ?? 0,
      },
      {
        accessor: 'dueCount',
        label: t('esla_col_due'),
        // Resalta el conteo de vencidas con un Badge de error cuando hay > 0.
        render: (row) =>
          row.dueCount > 0 ? (
            <div className='d-flex align-items-start'>
              {Badge ? (
                <Badge text={String(row.dueCount)} color='error' size='sm' />
              ) : (
                <span>{row.dueCount}</span>
              )}
            </div>
          ) : (
            (row.dueCount ?? 0)
          ),
      },
      {
        accessor: 'closedOnTime',
        label: t('esla_col_on_time'),
        render: (row) => row.closedOnTime ?? 0,
      },
      {
        accessor: 'closedLate',
        label: t('esla_col_late'),
        render: (row) => row.closedLate ?? 0,
      },
      {
        accessor: 'avgCloseBusinessDays',
        label: t('esla_col_avg_days'),
        render: (row) => formatAvg(row.avgCloseBusinessDays),
      },
    ],
    [t, Badge]
  );

  const ContentWrapper = Card || 'div';
  const contentWrapperProps = Card
    ? { specialPadding: 'p-4' }
    : { className: 'container-fluid p-4' };

  return (
    <div>
      <div className='mb-4 d-flex justify-content-between align-items-start'>
        <div>
          <h2 className='fw-semibold'>{t('esla_title')}</h2>
          <h4 className='fw-normal'>{t('esla_subtitle')}</h4>
        </div>
        {IconButton && (
          <IconButton
            ariaLabel={t('esla_refresh')}
            icon='arrowCounterclockwise'
            size='lg'
            iconFill='var(--primary-500)'
            onClick={fetchSummary}
            disabled={loading}
          />
        )}
      </div>

      <ContentWrapper {...contentWrapperProps}>
        {error && (
          <div className='alert alert-danger py-2' style={{ fontSize: 13 }}>
            {error}
            {Button && (
              <div className='mt-2'>
                <Button
                  label={t('esla_refresh')}
                  size='sm'
                  variant='secondary'
                  onClick={fetchSummary}
                />
              </div>
            )}
          </div>
        )}

        {/* Filtros client-side: búsqueda por validador + "Solo con vencidos". */}
        <div className='d-flex flex-wrap gap-2 mb-3'>
          <div style={{ flex: '0 1 280px' }}>
            {Input ? (
              <Input
                value={search}
                onChange={(e) =>
                  setSearch(e?.target ? e.target.value : (e ?? ''))
                }
                placeholder={t('esla_filter_user_placeholder')}
                width='100%'
                disabled={loading}
              />
            ) : (
              <input
                type='text'
                className='form-control form-control-sm'
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t('esla_filter_user_placeholder')}
              />
            )}
          </div>
          <div style={{ flex: '0 1 220px' }}>
            {Dropdown ? (
              <Dropdown
                options={dueOptions}
                value={selectedDueOption}
                onChange={(option) => setDueFilter(option?.value || 'all')}
                placeholder={t('esla_filter_due_only')}
                width='100%'
                isDisabled={loading}
              />
            ) : (
              <select
                className='form-select form-select-sm'
                value={dueFilter}
                onChange={(e) => setDueFilter(e.target.value)}
              >
                {dueOptions.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            )}
          </div>
        </div>

        {loading && !summary ? (
          <div className='d-flex flex-column gap-2'>
            {Skeleton ? (
              [0, 1, 2, 3, 4].map((i) => (
                <Skeleton key={i} height='2.5rem' width='100%' />
              ))
            ) : (
              <p className='font-light'>{t('esla_loading')}</p>
            )}
          </div>
        ) : (
          <>
            <div className='table-container na-eepp-grid-6-content'>
              {Table ? (
                <Table
                  columns={columns}
                  data={filteredRows}
                  loading={loading}
                />
              ) : (
                <p className='font-light'>
                  {loading ? t('esla_loading') : null}
                </p>
              )}
              {!loading && filteredRows.length === 0 && (
                <div className='text-center p-4'>
                  <p className='caption font-light'>{t('esla_empty')}</p>
                </div>
              )}
            </div>

            {/* Bloque "Vencidos por etapa": un chip por etapa abierta del flujo. */}
            {dueByStage.length > 0 && (
              <div className='mt-4'>
                <h5 className='mb-2'>{t('esla_by_stage_title')}</h5>
                <div className='d-flex flex-wrap' style={{ gap: 8 }}>
                  {dueByStage.map((stage) => {
                    const label = t(`esla_stage_${stage.stage}`) || stage.stage;
                    const dueCount = stage.dueCount ?? 0;
                    return (
                      <div
                        key={stage.stage}
                        className='d-flex align-items-center'
                        style={{
                          gap: 8,
                          padding: '6px 10px',
                          border: '1px solid #E2E8F0',
                          borderRadius: 8,
                        }}
                      >
                        <span className='fw-semibold' style={{ fontSize: 13 }}>
                          {label}
                        </span>
                        <span
                          className='mini font-light'
                          style={{ color: '#64748B' }}
                        >
                          {stage.openCount ?? 0} {t('esla_stage_open_label')}
                        </span>
                        {Badge ? (
                          <Badge
                            text={`${dueCount} ${t('esla_stage_due_label')}`}
                            color={dueCount > 0 ? 'error' : 'light'}
                            size='sm'
                          />
                        ) : (
                          <span>
                            {dueCount} {t('esla_stage_due_label')}
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </>
        )}
      </ContentWrapper>
    </div>
  );
}
